import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEye } from "react-icons/fi";
import { IoMdSearch } from "react-icons/io";
import { TbTableColumn } from "react-icons/tb";
// import { toast } from "react-toastify";
import moment from "moment";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";

import { getallPurchaseOrders } from "../../../../Redux/actions/Operationsactions/getpurchaseorders";
// import { DeletePurchaseOrderApi } from "../../../../Api/Operations/PurchaseOrders";

import ActionButton from "../../../Common/ActionButton";
import { filterStoreProjects } from "../../projectFilters";
// Details are now a dedicated page: PurchaseOrderDetailsPage

/**
 * Purchase Orders List Component
 */
const PurchaseOrdersListComp = () => {
  const resultAllPOs = useSelector((state) => state.getAllPurchaseOrders);
  const rolesData = useSelector((state) => state.GetAllRole);
  const history = useHistory();
  const projects = filterStoreProjects(rolesData?.data?.Projects);
  // Empty = all projects for the business.
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const gridRef = useRef(null);
  const colDropdownRef = useRef(null);
  const [colDropdownOpen, setColDropdownOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();

  // navigate to details page
  const openDetails = useCallback(
    (id) => {
      // navigate to the new full-screen details page
      history.push(`/procurement/purchase-order/details/${id}`);
    },
    [history],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (params) => params.value,
      valueFormatter: (params) =>
        params.value === null || params.value === undefined || params.value === ""
          ? "N/A"
          : params.value,
    }),
    [],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "PO Number",
        field: "PoNumber",
        flex: 1,
        maxWidth: 220,
      },
      {
        headerName: "Vendor",
        field: "VendorName",
        flex: 1,
        // maxWidth: 220,
      },
      {
        headerName: "Project",
        field: "ProjectName",
        flex: 1,
        maxWidth: 220,
      },
      {
        headerName: "Amount",
        field: "GrossAmount",
        flex: 1,
        maxWidth: 220,
        valueFormatter: (params) =>
          params.value ? `Rs. ${params.value?.toLocaleString()}` : "N/A",
      },
      {
        headerName: "Order Date",
        field: "PoDate",
        flex: 1,
        maxWidth: 220,
        valueFormatter: (params) =>
          params.value ? moment(params.value).format("YYYY-MM-DD") : "N/A",
      },
      {
        headerName: "Status",
        field: "Status",
        flex: 1,
        maxWidth: 220,
        cellRenderer: (params) => {
          if (!params.data || !params.value) {
            return "N/A";
          }
          return <StatusChip status={params.value} type={statusChipType(params.value)} />;
        },
      },
      ...(rolesData?.data?.UserRoles?.includes("Store Purchase Orders_Edit")
        ? [
            {
              headerName: "Action",
              colId: "action",
              width: 120,
              filter: false,
              sortable: false,
              headerComponent: () => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  {rolesData?.data?.UserRoles?.includes("Store Purchase Orders_Create") ? (
                    <ActionButton
                      text="New+"
                      newBtn="true"
                      onClick={() => history.push("/procurement/purchase-order/add")}
                    />
                  ) : (
                    <span>Action</span>
                  )}
                </div>
              ),
              cellRenderer: (params) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    gap: "8px",
                  }}
                >
                  <FiEye
                    title="Details"
                    size="1.1em"
                    style={{ cursor: "pointer", color: "#4ac8d1" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // open details modal; tolerate either Id or PurchaseOrderId
                      const id = params.data?.Id || params.data?.PurchaseOrderId;
                      openDetails(id);
                    }}
                  />
                </div>
              ),
            },
          ]
        : []),
    ],
    [rolesData, history, openDetails],
  );

  useEffect(() => {
    dispatch(getallPurchaseOrders(selectedProjectId || undefined));
  }, [dispatch, selectedProjectId]);

  const onGridReady = useCallback((params) => {
    const visibilityMap = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        visibilityMap[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(visibilityMap);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({
        ...prev,
        [colId]: !isCurrentlyVisible,
      }));
    },
    [columnVisibility],
  );

  // const handleDelete = async (poId) => {
  //   if (window.confirm("Are you sure you want to delete this purchase order?")) {
  //     try {
  //       const result = await DeletePurchaseOrderApi(poId);
  //       if (result.data?.IsSuccess) {
  //         toast.success("Purchase Order deleted successfully");
  //         dispatch(getallPurchaseOrders(projectId));
  //       } else {
  //         toast.error(result.data?.Message || "Failed to delete");
  //       }
  //     } catch (error) {
  //       toast.error("Error deleting purchase order");
  //     }
  //   }
  // };

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Purchase Orders"
        >
          Purchase Orders
        </div>
        <div className="d-flex align-items-center max-w-250" style={{ position: "relative" }}>
          <IoMdSearch
            size="1.1em"
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#4e545a",
            }}
          />
          <input
            type="text"
            className="input_styling"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "250px", paddingLeft: 30 }}
            autoComplete="off"
          />
        </div>

        <select
          className="input_styling"
          style={{ marginLeft: "10px", width: "200px" }}
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.ProjectId} value={p.ProjectId}>
              {p.ProjectName}
            </option>
          ))}
        </select>

        <div ref={colDropdownRef} style={{ position: "relative", flexGrow: 0 }}>
          <button
            className="input_styling"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              padding: "4px 10px",
              whiteSpace: "nowrap",
              marginLeft: "10px",
            }}
            onClick={() => setColDropdownOpen((prev) => !prev)}
            title="Show / Hide Columns"
          >
            <TbTableColumn size="1.3em" />
            <span>Columns</span>
          </button>

          {colDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "110%",
                right: 0,
                zIndex: 9999,
                background: "#fff",
                border: "1px solid #E8FBFB",
                borderRadius: 6,
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                padding: "8px 0",
                minWidth: 180,
              }}
            >
              {columnDefs.map((col) => (
                <label
                  key={col.colId || col.field}
                  style={{
                    display: "block",
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={columnVisibility[col.colId || col.field] !== false}
                    onChange={() => toggleColumn(col.colId || col.field)}
                    style={{ marginRight: "8px" }}
                  />
                  {col.headerName}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        style={{ width: "100%", height: "800px", marginTop: "20px" }}
        // className="ag-theme-quartz"
        className="ag-theme-alpine"
      >
        <AgGridReact
          ref={gridRef}
          rowData={resultAllPOs?.data || []}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={(params) => {
            // still call original handler to populate column visibility
            onGridReady(params);
          }}
          pagination={true}
          paginationPageSize={10}
          quickFilterText={searchTerm}
        />
      </div>
      {/* Details are now a full page at /procurement/purchase-order/details/:id */}
    </>
  );
};

export { PurchaseOrdersListComp };
